import type { DeliveryDay } from '../lib/api';

/**
 * Day-by-day delivery chart: impressions and clicks against date, on a DUAL value axis — impressions
 * scaled to the LEFT axis, clicks to the RIGHT — so both series read clearly even when impressions
 * dwarf clicks (the usual case). Pure inline SVG, sized by a viewBox. A provisional day (still inside
 * the settle window) is drawn with a hollow marker, and each point carries a <title> for hover detail.
 */

const IMP = '#2b5fa8';   // impressions — left axis, primary blue
const CLK = '#e0533d';   // clicks — right axis, accent

const compact = (n: number): string =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  : n >= 1_000 ? `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`
  : `${n}`;

const mmdd = (iso: string): string => {
  const p = iso.slice(0, 10).split('-');
  return p.length === 3 ? `${p[1]}/${p[2]}` : iso;
};

export function DeliveryDayChart({ byDay, height = 240 }: { byDay: DeliveryDay[]; height?: number }) {
  if (!byDay.length) return null;

  const W = 720, H = height;
  const padL = 54, padR = 54, padT = 20, padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = byDay.length;

  // Independent scales: impressions on the left axis, clicks on the right.
  const maxImp = Math.max(1, ...byDay.map((d) => d.impressions));
  const maxClk = Math.max(1, ...byDay.map((d) => d.clicks));
  const x = (i: number) => (n === 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW);
  const yImp = (v: number) => padT + plotH - (v / maxImp) * plotH;
  const yClk = (v: number) => padT + plotH - (v / maxClk) * plotH;

  const path = (key: 'impressions' | 'clicks', scale: (v: number) => number) =>
    byDay.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${scale(d[key]).toFixed(1)}`).join(' ');

  // Shared horizontal gridlines at 0/25/50/75/100 %, labelled with each axis's own value.
  const fracs = [0, 0.25, 0.5, 0.75, 1];
  const rowY = (f: number) => padT + plotH - f * plotH;

  const step = Math.max(1, Math.ceil(n / 7));
  const xLabels = byDay.map((d, i) => ({ i, d })).filter(({ i }) => i % step === 0 || i === n - 1);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: '1rem', margin: '0 0 0.35rem', fontSize: '0.8rem' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
          <span style={{ width: 12, height: 3, background: IMP, borderRadius: 2 }} /> Impressions <span style={{ color: '#999' }}>(left)</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
          <span style={{ width: 12, height: 3, background: CLK, borderRadius: 2 }} /> Clicks <span style={{ color: '#999' }}>(right)</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
           style={{ display: 'block', maxWidth: '100%' }}
           aria-label={`Impressions (left axis) and clicks (right axis) by day over ${n} day(s)`}>
        {/* Gridlines + dual-axis labels: impressions on the left in blue, clicks on the right in red */}
        {fracs.map((f, k) => (
          <g key={k}>
            <line x1={padL} y1={rowY(f)} x2={W - padR} y2={rowY(f)} stroke="#e8e8e8" strokeWidth={1} />
            <text x={padL - 6} y={rowY(f) + 3} textAnchor="end" fontSize={11} fill={IMP}>{compact(Math.round(maxImp * f))}</text>
            <text x={W - padR + 6} y={rowY(f) + 3} textAnchor="start" fontSize={11} fill={CLK}>{compact(Math.round(maxClk * f))}</text>
          </g>
        ))}
        {/* Axis lines (left blue, right red) */}
        <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke={IMP} strokeWidth={1} opacity={0.5} />
        <line x1={W - padR} y1={padT} x2={W - padR} y2={padT + plotH} stroke={CLK} strokeWidth={1} opacity={0.5} />
        <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke="#cfd6df" strokeWidth={1} />
        {/* X labels */}
        {xLabels.map(({ i, d }) => (
          <text key={i} x={x(i)} y={padT + plotH + 16} textAnchor="middle" fontSize={11} fill="#777">{mmdd(d.date)}</text>
        ))}
        {/* Series (impressions -> left scale, clicks -> right scale) */}
        <path d={path('impressions', yImp)} fill="none" stroke={IMP} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        <path d={path('clicks', yClk)} fill="none" stroke={CLK} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {/* Points (hollow when provisional), with hover detail */}
        {byDay.map((d, i) => (
          <g key={d.date}>
            <circle cx={x(i)} cy={yImp(d.impressions)} r={3} fill={d.settled ? IMP : '#fff'} stroke={IMP} strokeWidth={1.5}>
              <title>{`${d.date}: ${d.impressions.toLocaleString('en-US')} impressions${d.settled ? '' : ' (provisional)'}`}</title>
            </circle>
            <circle cx={x(i)} cy={yClk(d.clicks)} r={3} fill={d.settled ? CLK : '#fff'} stroke={CLK} strokeWidth={1.5}>
              <title>{`${d.date}: ${d.clicks.toLocaleString('en-US')} clicks${d.settled ? '' : ' (provisional)'}`}</title>
            </circle>
          </g>
        ))}
      </svg>
    </div>
  );
}
